import { conditions } from "../../helpers/conditions/conditions";
import { getUUID, responseValidator } from "../../helpers/helpers";
import { ConditionName } from "../../interfaces/enums";
import { IComments } from "../../interfaces/interfaces";
import { apiCaller, get } from "../../services/apiCaller";
import { API } from "../../services/services";
import {
    setCanAddComment,
    setCanShowSubmittedComment,
    setComments,
    setCommentsCount,
    setCommentsCountPagination,
    setRealTimeReply,
    setReplyToAuthor,
    setUsersCommentsProfiles
} from "../slices/commentsSlice";
import store from "../store";
import { setCommentPopover } from "../slices/popoversSlice";
import { toast } from "mobin-toast";
import { getCommentList, submitComment } from "./videoData.actions";
import axios from "axios";
import { JwtPayload, jwtDecode } from "jwt-decode";

export const checkCanComment = async () => {
    const dispatch = store.dispatch;
    const { details } = store.getState().videoData;
    const { data, status } = await apiCaller({ url: API().comment.canAddComment(details.content_id!) });
    if (responseValidator(status)) {
        dispatch(setCanAddComment(data.can_comment));
        dispatch(setCanShowSubmittedComment(data.comment_hidden_default === 0));
        dispatch(setCommentsCount(data.comments_count - data.hidden_comments_count));
        dispatch(setCommentsCountPagination(data.first_level_comments_count - data.first_level_hidden_comments_count));
    }
};

export const handleGetComments = async (offset: number, setScrollEnd: (value: boolean) => void) => {
    const dispatch = store.dispatch;
    const { comments, usersCommentsProfiles } = store.getState().comments;
    const { details } = store.getState().videoData;
    const { data, status } = await getCommentList(API().comment.commentList(details.content_id!), offset);
    if (responseValidator(status) && data.results && data.results.length) {
        if (comments !== undefined && comments.length) dispatch(setComments([...comments, ...data.results]));
        else dispatch(setComments(data.results));
        setScrollEnd(false);
        dispatch(setUsersCommentsProfiles(await handleGetUsersComments(data.results, usersCommentsProfiles)));
    } else if (comments === undefined || comments.length === 0) dispatch(setComments([]));
};

export const handleGetReplies = async (
    commentId: number,
    replies: IComments[],
    setReplies: (value: IComments[]) => void,
    usersRepliesProfiles: any[],
    setUsersRepliesProfiles: (values: any[]) => void,
    realTimeReply?: undefined | IComments
) => {
    get(
        async (data: any, status) => {
            if (responseValidator(status)) {
                if (replies.length) setReplies([...replies, ...data.results]);
                else setReplies(data.results);
                setUsersRepliesProfiles(await handleGetUsersComments(data.results, usersRepliesProfiles));
            }
        },
        API().comment.replyList(commentId),
        {
            limit: 10,
            offset: replies.length - (realTimeReply ? 1 : 0)
        }
    );
};

export const handleSubmitDefaultMessageComment = async (messageId: number) => {
    const dispatch = store.dispatch;
    const { details } = store.getState().videoData;
    const { comments } = store.getState().comments;
    await checkCanComment();
    if (conditions(ConditionName.canAddComment)) {
        const commentData = {
            text: undefined,
            default_message_id: messageId,
            content: details.content_id,
            reply_to: undefined
        };
        const { data, status } = await submitComment(API().comment.create, commentData);
        if (responseValidator(status)) {
            if (comments) {
                dispatch(setComments([data, ...comments]));
                if (conditions(ConditionName.isTouchScreen) && comments.length > 0)
                    document.getElementById("touchscreenListComments")!.scrollIntoView({ behavior: "smooth" });
            } else dispatch(setComments([data]));
        }
    }
};

export const handleSubmitComment = async (
    inputValue: string,
    setInputValue: (value: string) => void,
    selectedEmoji: string,
    setSelectedEmoji: (value: string) => void,
    type: string,
    setShowReplyBox: any
) => {
    const dispatch = store.dispatch;
    const { details } = store.getState().videoData;
    const { replyToAuthor, comments } = store.getState().comments;

    await checkCanComment();
    if (conditions(ConditionName.canAddComment) && (inputValue || selectedEmoji)) {
        const commentData = {
            text: inputValue,
            content: type === "reply" ? undefined : details.content_id,
            reply_to: type === "reply" ? replyToAuthor.id : undefined
        };
        const { data, status } = await submitComment(API().comment.create, commentData);
        if (responseValidator(status)) {
            setInputValue("");
            setSelectedEmoji("");
            if (conditions(ConditionName.canShowSubmittedComment)) {
                if (type === "reply") dispatch(setRealTimeReply(data));
                else if (comments) {
                    dispatch(setComments([data, ...comments]));
                    if (conditions(ConditionName.isTouchScreen) && comments.length > 0)
                        document.getElementById("touchscreenListComments")!.scrollIntoView({ behavior: "smooth" });
                } else dispatch(setComments([data]));
            } else toast({ title: `${type === "reply" ? "پاسخ" : "نظر"} شما با موفقیت ثبت شد و پس از تایید نمایش داده می شود.`, type: "success" });
            if (type === "reply") {
                dispatch(setReplyToAuthor({}));
                if (setShowReplyBox) setShowReplyBox(false);
            }
        } else toast({ title: "خطا در ثبت دیدگاه", type: "error" });
    } else if (!conditions(ConditionName.canAddComment)) toast({ title: "در حال حاضر امکان ثبت دیدگاه وجود ندارد", type: "error" });
};

export const handleGetUsersComments = async (comments: IComments[], usersProfiles: any[]) => {
    const usersCommentsIds: string[] = [];
    comments.map((comment: IComments) => comment.author !== "AnonymousUser" && usersCommentsIds.push(comment.author));
    const response = await axios({
        url: API().user.profile,
        method: "post",
        headers: { "Content-Type": "application/json" },
        data: usersCommentsIds
    });
    if (responseValidator(response.status)) return [...usersProfiles, ...response.data];
    else return [];
};

export const handleCommentsIdsInLocalStorage = () => {
    const commentsLikeUnlike = localStorage.getItem("commentsLikeUnlike");
    if (commentsLikeUnlike) {
        const { params } = store.getState().videoData;
        const currentVideo = JSON.parse(commentsLikeUnlike).find(
            (comment: any) => comment.videoId === params.id && comment.videoType === params.type
        );
        if (currentVideo) return currentVideo.comments;
    }
};

export const handleLikeUnlikeComment = async (
    commentId: number,
    likeStatus: boolean,
    setLikeStatus: (value: boolean) => void,
    likeCount: number,
    setLikeCount: (value: number) => void
) => {
    const { params } = store.getState().videoData;
    const { status } = await apiCaller({
        url: likeStatus ? API().comment.like(commentId) : API().comment.unlike(commentId),
        data: { uuid: getUUID() }
    });
    if (responseValidator(status)) {
        setLikeCount(likeStatus ? likeCount + 1 : likeCount - 1);
        setLikeStatus(likeStatus);
        const commentsLikeUnlike = localStorage.getItem("commentsLikeUnlike");
        if (commentsLikeUnlike) {
            const filteredVideos = JSON.parse(commentsLikeUnlike).filter(
                (comment: any) => comment.videoId !== params.id || (comment.videoId === params.id && comment.videoType !== params.type)
            );
            const currentVideo = JSON.parse(commentsLikeUnlike).find(
                (comment: any) => comment.videoId === params.id && comment.videoType === params.type
            );
            if (filteredVideos) {
                if (currentVideo) {
                    const filteredComments = currentVideo.comments.filter((comment: number) => comment !== commentId);
                    if (likeStatus)
                        localStorage.setItem(
                            "commentsLikeUnlike",
                            JSON.stringify([
                                ...filteredVideos,
                                { videoId: params.id, videoType: params.type, comments: [...filteredComments, commentId] }
                            ])
                        );
                    else
                        localStorage.setItem(
                            "commentsLikeUnlike",
                            JSON.stringify([...filteredVideos, { videoId: params.id, videoType: params.type, comments: filteredComments }])
                        );
                } else {
                    localStorage.setItem(
                        "commentsLikeUnlike",
                        JSON.stringify([...filteredVideos, { videoId: params.id, videoType: params.type, comments: [commentId] }])
                    );
                }
            }
        } else localStorage.setItem("commentsLikeUnlike", JSON.stringify([{ videoId: params.id, videoType: params.type, comments: [commentId] }]));
    }
};

export const handleCloseCommentPopover = () => {
    const dispatch = store.dispatch;
    dispatch(setCommentPopover(false));
    dispatch(setComments(undefined));
};

export const isShowCommentSettings = (userId: string) => {
    const { params } = store.getState().videoData;
    if (!params.xToken) return false;
    else {
        const decodeToken = jwtDecode<JwtPayload>(params.xToken);
        return decodeToken.sub === userId;
    }
};
